import type { Lang } from '../registry.ts';
import { ca, type Dict } from './ca.ts';
import { es } from './es.ts';
import { gl } from './gl.ts';

export type { Dict };

export const DICTS: Record<Lang, Dict> = { ca, es, gl };

let current: Lang = 'ca';

export function setLang(lang: Lang): void {
  current = lang;
}

export function getLang(): Lang {
  return current;
}

/** Dictionary for the active language. */
export function t(): Dict {
  return DICTS[current];
}

/** Replaces {name} placeholders. Unknown placeholders are left untouched. */
export function fmt(template: string, params: Record<string, string | number> = {}): string {
  return template.replace(/\{(\w+)\}/g, (match, key: string) => (key in params ? String(params[key]) : match));
}

const BCP47: Record<Lang, string> = { ca: 'ca-ES', es: 'es-ES', gl: 'gl-ES' };

export function locale(lang: Lang = current): string {
  return BCP47[lang];
}

export function formatBytes(bytes: number, lang: Lang = current): string {
  const units = ['B', 'KB', 'MB', 'GB'];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit++;
  }
  const digits = unit === 0 || value >= 100 ? 0 : 1;
  return `${new Intl.NumberFormat(locale(lang), { maximumFractionDigits: digits }).format(value)} ${units[unit]}`;
}

export function formatPercent(ratio: number, lang: Lang = current): string {
  return new Intl.NumberFormat(locale(lang), { style: 'percent', maximumFractionDigits: 0 }).format(ratio);
}

/** Picks the best supported language from the browser preferences. */
export function detectLang(languages: readonly string[]): Lang {
  for (const l of languages) {
    const code = l.toLowerCase().split('-')[0];
    if (code === 'ca' || code === 'es' || code === 'gl') return code;
    // Valencian / Balearic variants map to Catalan.
    if (l.toLowerCase().startsWith('ca')) return 'ca';
  }
  return 'ca';
}
